/**
 * API client for the abet-mlb-proxy Cloudflare Worker.
 * All HF Space requests go through the authenticated Worker proxy.
 */

const PROXY = import.meta.env.VITE_PROXY_URL || 'https://abet-mlb-proxy.abetco.workers.dev'

/**
 * Fetch data from the CF Worker proxy.
 * Returns { data, status, message } where status is 'ok' | 'sleeping' | 'starting' | 'timeout' | 'error'
 */
export async function fetchFromProxy(endpoint, params = {}) {
  try {
    const url = new URL(`${PROXY}${endpoint}`)
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) url.searchParams.set(k, v)
    })

    const resp = await fetch(url.toString(), {
      signal: AbortSignal.timeout(18000),
    })

    const data = await resp.json()

    // Worker returns structured error responses for sleeping/timeout/error
    if (!resp.ok) {
      return {
        data: null,
        status: data.status || 'error',
        message: data.message || `HTTP ${resp.status}`,
        hfStage: data.hf_stage || null,
      }
    }

    return { data, status: 'ok', message: null, hfStage: null }

  } catch (err) {
    const isTimeout = err.name === 'TimeoutError' || err.message?.includes('timeout')
    return {
      data: null,
      status: isTimeout ? 'timeout' : 'error',
      message: isTimeout ? 'Request timed out (18s)' : err.message,
      hfStage: null,
    }
  }
}

/** Convenience wrappers */
export const fetchHealth = () => fetchFromProxy('/health')
export const fetchEvents = (params = {}) => fetchFromProxy('/events', params)
export const fetchKeys = () => fetchFromProxy('/keys')
export const fetchOdds = (gameId) => fetchFromProxy('/odds', gameId ? { game_id: gameId } : {})
export const fetchPosts = (params = {}) => fetchFromProxy('/posts', params)
export const fetchWorkerStatus = () => fetchFromProxy('/status')
export const fetchMLStats = () => fetchFromProxy('/ml-stats')

/** Metrics collector endpoints */
const METRICS_PROXY = import.meta.env.VITE_METRICS_PROXY_URL || 'https://abet-metrics-collector.abetco.workers.dev'

export async function fetchMetricsStatus() {
  try {
    const resp = await fetch(`${METRICS_PROXY}/status`, { signal: AbortSignal.timeout(15000) })
    if (!resp.ok) return { data: null, status: 'error', message: `HTTP ${resp.status}` }
    const data = await resp.json()
    return { data, status: 'ok' }
  } catch (err) {
    return { data: null, status: 'error', message: err.message }
  }
}

export async function fetchMetricsHistory(domain, metric, hours = 24) {
  try {
    const url = new URL(`${METRICS_PROXY}/metrics`)
    if (domain) url.searchParams.set('domain', domain)
    if (metric) url.searchParams.set('metric', metric)
    url.searchParams.set('hours', hours)
    const resp = await fetch(url.toString(), { signal: AbortSignal.timeout(15000) })
    if (!resp.ok) return { data: [], status: 'error' }
    const result = await resp.json()
    return { data: result.data || [], status: 'ok', count: result.count }
  } catch (err) {
    return { data: [], status: 'error', message: err.message }
  }
}

export async function fetchAlertHistory(hours = 24) {
  try {
    const resp = await fetch(`${METRICS_PROXY}/alerts?hours=${hours}`, { signal: AbortSignal.timeout(15000) })
    if (!resp.ok) return { data: [], status: 'error' }
    const result = await resp.json()
    return { data: result.data || [], status: 'ok', count: result.count }
  } catch (err) {
    return { data: [], status: 'error', message: err.message }
  }
}

/** Fetch today's MLB schedule directly from the MLB Stats API (public, no key needed) */
export async function fetchMLBSchedule() {
  try {
    const today = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
    const url = `https://statsapi.mlb.com/api/v1/schedule?sportId=1&date=${today}`
    const resp = await fetch(url, { signal: AbortSignal.timeout(10000) })
    if (!resp.ok) return { games: [], total: 0 }
    const data = await resp.json()
    const games = data.dates?.[0]?.games || []
    return {
      total: games.length,
      games: games.map(g => ({
        gamePk: g.gamePk,
        away: g.teams.away.team.name,
        home: g.teams.home.team.name,
        status: g.status.detailedState,
        statusCode: g.status.statusCode,
        startTime: g.gameDate,
      })),
    }
  } catch {
    return { games: [], total: 0 }
  }
}

// ---------------------------------------------------------------------------
// TheOddsAPI proxy (abet-theoddsapi-proxy CF Worker)
// ---------------------------------------------------------------------------
const ODDS_PROXY = import.meta.env.VITE_ODDS_PROXY_URL || 'https://abet-theoddsapi-proxy.abetco.workers.dev'

/**
 * Fetch data from the TheOddsAPI CF Worker proxy.
 * Returns { data, status, message, remaining, used }
 */
export async function fetchFromOddsProxy(endpoint, params = {}) {
  try {
    const url = new URL(`${ODDS_PROXY}${endpoint}`)
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v)
    })

    const resp = await fetch(url.toString(), {
      signal: AbortSignal.timeout(18000),
    })

    const remaining = resp.headers.get('X-Odds-Requests-Remaining')
    const used = resp.headers.get('X-Odds-Requests-Used')

    const data = await resp.json()

    if (!resp.ok) {
      return {
        data: null,
        status: data.status || 'error',
        message: data.message || `HTTP ${resp.status}`,
        remaining, used,
      }
    }

    return { data, status: 'ok', message: null, remaining, used }

  } catch (err) {
    const isTimeout = err.name === 'TimeoutError' || err.message?.includes('timeout')
    return {
      data: null,
      status: isTimeout ? 'timeout' : 'error',
      message: isTimeout ? 'Request timed out (18s)' : err.message,
      remaining: null, used: null,
    }
  }
}

/** TheOddsAPI convenience wrappers */
export const fetchOddsSports = () => fetchFromOddsProxy('/sports')
export const fetchOddsEvents = (sport, params = {}) => fetchFromOddsProxy(`/events/${sport}`, params)
export const fetchOddsForSport = (sport, params = {}) => fetchFromOddsProxy(`/odds/${sport}`, params)
export const fetchEventOdds = (sport, eventId, params = {}) => fetchFromOddsProxy(`/event-odds/${sport}/${eventId}`, params)
export const fetchOddsScores = (sport, params = {}) => fetchFromOddsProxy(`/scores/${sport}`, params)
export const fetchOddsParticipants = (sport) => fetchFromOddsProxy(`/participants/${sport}`)
export const fetchOddsHistorical = (sport, params = {}) => fetchFromOddsProxy(`/historical/${sport}`, params)
export const fetchOddsProxyStatus = () => fetchFromOddsProxy('/status')

/** Build full URL for a saved image through the CF Worker proxy */
export function imageUrl(relativePath) {
  if (!relativePath) return null
  return `${PROXY}/images/${relativePath}`
}

// ---------------------------------------------------------------------------
// Receiver proxy (abet-receiver-proxy CF Worker) — live odds from Oracle VM
// ---------------------------------------------------------------------------
const RECEIVER_PROXY = import.meta.env.VITE_RECEIVER_PROXY_URL || 'https://abet-receiver-proxy.abetco.workers.dev'
const RECEIVER_PROXY_TIMEOUT_MS = 25000

function buildReceiverProxyUrl(endpoint, params = {}) {
  const cleanEndpoint = String(endpoint || '').replace(/^\/+/, '')
  const url = RECEIVER_PROXY
    ? new URL(`${RECEIVER_PROXY}${endpoint}`)
    : new URL('/api/oracle-vm', window.location.origin)

  if (!RECEIVER_PROXY) {
    url.searchParams.set('endpoint', cleanEndpoint)
  }

  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v)
  })

  return url
}

/**
 * Fetch data from the receiver CF Worker proxy.
 * Returns { data, status, message }
 */
export async function fetchFromReceiverProxy(endpoint, params = {}, options = {}) {
  const timeoutMs = Number(options.timeoutMs || RECEIVER_PROXY_TIMEOUT_MS)
  try {
    const url = buildReceiverProxyUrl(endpoint, params)

    const resp = await fetch(url.toString(), {
      signal: AbortSignal.timeout(timeoutMs),
    })

    const data = await resp.json()

    if (!resp.ok) {
      return {
        data: null,
        status: data.error ? 'error' : 'unknown',
        message: data.message || data.error || `HTTP ${resp.status}`,
      }
    }

    return { data, status: 'ok', message: null }

  } catch (err) {
    const isTimeout = err.name === 'TimeoutError' || err.message?.includes('timeout')
    return {
      data: null,
      status: isTimeout ? 'timeout' : 'error',
      message: isTimeout ? `Request timed out (${Math.round(timeoutMs / 1000)}s)` : err.message,
    }
  }
}

/** Receiver convenience wrappers */
export const fetchReceiverHealth = () => fetchFromReceiverProxy('/health')
export const fetchReceiverStats = () => fetchFromReceiverProxy('/stats')
export const fetchReceiverBoard = (params = {}) => fetchFromReceiverProxy('/board', params, { timeoutMs: 25000 })
export const fetchReceiverOdds = (params = {}, options = {}) => fetchFromReceiverProxy('/odds', params, options)
export const fetchReceiverGames = () => fetchFromReceiverProxy('/games')

export function buildReceiverStreamUrl(params = {}) {
  const baseUrl = RECEIVER_PROXY
    ? new URL(RECEIVER_PROXY)
    : new URL(window.location.origin)

  const wsUrl = new URL('/ws', baseUrl)
  wsUrl.protocol = wsUrl.protocol === 'https:' ? 'wss:' : 'ws:'

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') wsUrl.searchParams.set(key, value)
  })

  return wsUrl.toString()
}

export async function fetchRuntimeBoardSnapshot(sport) {
  if (!sport || sport === 'ALL') {
    return { data: null, status: 'skip', message: null }
  }

  try {
    const resp = await fetch(`/data/runtime-board/${sport}.generated.json`, {
      signal: AbortSignal.timeout(5000),
    })

    if (!resp.ok) {
      return { data: null, status: 'error', message: `HTTP ${resp.status}` }
    }

    const data = await resp.json()
    return { data, status: 'ok', message: null }
  } catch (err) {
    const isTimeout = err.name === 'TimeoutError' || err.message?.includes('timeout')
    return {
      data: null,
      status: isTimeout ? 'timeout' : 'error',
      message: isTimeout ? 'Runtime board snapshot timed out (5s)' : err.message,
    }
  }
}

/**
 * UmpScorecards home-plate umpire performance data.
 *
 * Goes through the abet-mlb-proxy Worker rather than calling umpscorecards.com
 * directly: that host returns NO Access-Control-Allow-Origin header, so a
 * browser fetch is blocked outright. The Worker adds CORS and a 6h edge cache,
 * so we hit a free third-party service once per TTL instead of once per visitor.
 *
 * The join key is the umpire's NAME — the upstream payload has no id field.
 * Measured 2026-08-14: 71/71 real home-plate umpires from MLB StatsAPI matched
 * exactly. A miss is expected eventually and is NOT an error; callers must
 * render without the enrichment.
 *
 * Returns { byName: Map<string, row>, profileUrlBase, count, attribution }
 * or null. Never throws.
 */
export async function fetchUmpireScorecards() {
  try {
    const resp = await fetch(`${PROXY}/umpire-scorecards`, {
      signal: AbortSignal.timeout(15000),
    })
    if (!resp.ok) return null

    const data = await resp.json()
    const rows = Array.isArray(data?.rows) ? data.rows : []
    if (!rows.length) return null

    const byName = new Map()
    for (const row of rows) {
      if (row?.umpire) byName.set(String(row.umpire).trim(), row)
    }

    return {
      byName,
      count: data.count ?? byName.size,
      attribution: data.attribution || 'Data from UmpScorecards',
      profileUrlBase:
        data.profile_url_base || 'https://umpscorecards.com/data/single-umpire/',
      fetchedAt: data.fetched_at || null,
    }
  } catch {
    // Optional enrichment — a failure must never break the umpire widget.
    return null
  }
}

/** Public scorecard page for an umpire name. */
export function umpireProfileUrl(name, base) {
  const root = base || 'https://umpscorecards.com/data/single-umpire/'
  return root + encodeURIComponent(String(name || '').trim())
}
